import React, { Fragment, useMemo, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";

// --- Types ---
type Option = {
  id: string | number;
  label: string;
  value: string | number;
  // You can extend with any extra fields you need
  [key: string]: any;
};

// --- Utils ---
function normalize(text: string) {
  return (text ?? "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // strip accents
}

// --- Component ---
type SearchableDropdownProps = {
  options: Option[];
  value: Option | null;
  onChange: (opt: Option | null) => void;
  onInputChange?: (inputValue: string) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  label?: string;
  className?: string;
  isLoading?: boolean;
  noResultsText?: string;
};

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.333a1 1 0 0 1-1.445 0L3.29 9.983a1 1 0 1 1 1.42-1.407l3.017 3.05 6.53-6.6a1 1 0 0 1 1.447.264z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.25 8.27a.75.75 0 0 1-.02-1.06z" />
    </svg>
  );
}

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M6.28 6.22a.75.75 0 1 0-1.06 1.06L8.94 11l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 12.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 11l3.72-3.72a.75.75 0 1 0-1.06-1.06L10 9.94 6.28 6.22z" />
    </svg>
  );
}

export function SearchableDropdown({
  options,
  value,
  onChange,
  onInputChange,
  placeholder = "Search…",
  disabled = false,
  clearable = true,
  label,
  className = "",
  isLoading = false,
  noResultsText = "No results",
}: SearchableDropdownProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = normalize(query);
    return options.filter((o) => normalize(o.label).includes(q));
  }, [options, query]);

  return (
    <div className={`w-full ${className}`}>
      {label ? (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      ) : null}

      <Combobox value={value} onChange={onChange} disabled={disabled} nullable>
        {({ open }) => (
          <div className="relative">
            <div className="relative w-full">
              <Combobox.Button as="div" className="w-full">
                <Combobox.Input
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 pr-10 text-sm outline-none ring-2 ring-transparent transition focus:border-gray-400 focus:ring-gray-200 disabled:cursor-not-allowed disabled:bg-gray-100 hover:border-gray-400"
                  displayValue={(opt: Option) => opt?.label ?? ""}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    onInputChange?.(e.target.value);
                  }}
                  placeholder={placeholder}
                  autoComplete="off"
                />
              </Combobox.Button>

              {clearable && value ? (
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="absolute inset-y-0 right-7 my-auto grid h-6 w-6 place-items-center rounded-md text-gray-400 hover:text-gray-600"
                  aria-label="Clear selection"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              ) : null}

              <Combobox.Button className="absolute inset-y-0 right-1 my-auto grid h-7 w-7 place-items-center rounded-md text-gray-400 hover:text-gray-600">
                <ChevronIcon
                  className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </Combobox.Button>
            </div>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white p-1 text-sm shadow-xl focus:outline-none">
                {isLoading ? (
                  <div className="flex items-center gap-2 p-3 text-gray-500">
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                    Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-3 text-gray-500">{noResultsText}</div>
                ) : (
                  filtered.map((opt) => (
                    <Combobox.Option
                      key={opt.id}
                      value={opt}
                      className={({ active }) =>
                        `flex cursor-default select-none items-center gap-2 rounded-lg px-3 py-2 ${
                          active ? "bg-gray-100" : ""
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <CheckIcon
                            className={`h-4 w-4 text-gray-700 ${selected ? "opacity-100" : "opacity-0"}`}
                          />
                          <span className="truncate text-gray-900">
                            {opt.label}
                          </span>
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
          </div>
        )}
      </Combobox>
    </div>
  );
}
